import { getNumericalScaleOptionScores } from './answer-highlight';

function sumScore(actionValues) {
    if (!actionValues) return 0;
    return actionValues.reduce((sum, entry) => sum + (entry.score || 0), 0);
}

/**
 * Port of AssessmentsService.getCorrectAnswer — used for LMS preview answer highlighting.
 */
export function getCorrectAnswer(actions, blockType) {
    if (!actions) return {};

    let correctValue = null;
    let maxPossibleScore = 0;
    let values;
    let correctAnswer = {};

    if (blockType === 'yes-no-list') {
        Object.keys(actions).forEach((yesNoQuestionId) => {
            const yesNoQuestionValues = actions[yesNoQuestionId].values;
            Object.keys(yesNoQuestionValues).forEach((yesNoQuestionValue) => {
                const totalScore = sumScore(yesNoQuestionValues[yesNoQuestionValue]);
                if (
                    totalScore > maxPossibleScore ||
                    yesNoQuestionValues[yesNoQuestionValue].some((v) => v.correct)
                ) {
                    maxPossibleScore = totalScore;
                    correctValue = yesNoQuestionValue;
                }
            });
            correctAnswer[yesNoQuestionId] = {
                value: correctValue,
                score: maxPossibleScore,
            };
            maxPossibleScore = 0;
        });
    } else if (blockType === 'checkbox' || blockType === 'conversation') {
        Object.keys(actions.values || {}).forEach((checkboxOption) => {
            const totalScore = sumScore(actions.values[checkboxOption]);
            if (
                totalScore > 0 ||
                actions.values[checkboxOption].some((v) => v.correct)
            ) {
                correctAnswer[checkboxOption] = {
                    value: '1',
                    score: totalScore,
                };
            }
        });
    } else if (blockType === 'decision') {
        const decisions = actions.decisions || {};
        Object.keys(decisions).forEach((decisionQuestionKey) => {
            Object.keys(decisions[decisionQuestionKey].values).forEach((value) => {
                const totalScore = sumScore(
                    decisions[decisionQuestionKey].values[value]
                );
                if (
                    totalScore > 0 ||
                    decisions[decisionQuestionKey].values[value].some(
                        (v) => v.correct
                    )
                ) {
                    correctAnswer[decisionQuestionKey] = {
                        value,
                        score: totalScore,
                    };
                }
            });
        });
    } else if (actions.order) {
        values = Object.keys(actions.values || {});
        correctValue = actions.order;
        values.forEach((value) => {
            maxPossibleScore += sumScore(actions.values[value]);
        });
        correctAnswer = {
            value: correctValue,
            score: maxPossibleScore,
        };
    } else if (actions.values) {
        const correctByDefinition = {};
        values = Object.keys(actions.values);
        values.forEach((value) => {
            const totalScore = sumScore(actions.values[value]);
            if (totalScore > maxPossibleScore) {
                maxPossibleScore = totalScore;
                correctValue = value;
            }
            if (actions.values[value].some((v) => v.correct)) {
                correctByDefinition[value] = {
                    value,
                    score: actions.values[value][0].score,
                };
            }
        });

        correctAnswer = correctByDefinition;
        if (correctValue) {
            correctAnswer[correctValue] = {
                value: correctValue,
                score: maxPossibleScore,
            };
        }
    }

    return correctAnswer;
}

/**
 * Build per-block answer objects for highlight-only preview (correctAnswer, no userAnswer).
 */
export function buildHighlightBlockAnswers(question) {
    const actions = question?.actions || question?.definition?.actions;
    const blocks =
        question?.ui?.blocks || question?.definition?.ui?.blocks || [];
    if (!actions || !blocks.length) return {};

    const blockAnswers = {};

    blocks.forEach((block) => {
        const blockActionId = block.actionId;
        if (!blockActionId) return;

        if (block.type === 'groupOfBlocks') {
            const correctAnswer = {};
            (block.group || []).forEach((childBlock) => {
                const groupActionDefinition =
                    actions[blockActionId]?.[childBlock.actionId];
                correctAnswer[childBlock.actionId] = getCorrectAnswer(
                    groupActionDefinition,
                    childBlock.type
                );
            });
            blockAnswers[blockActionId] = {
                correctAnswer,
                userAnswer: {},
                highlightOnly: true,
            };
            return;
        }

        blockAnswers[blockActionId] = {
            correctAnswer:
                getCorrectAnswer(actions[blockActionId], block.type) || {},
            userAnswer: {},
            highlightOnly: true,
            ...(block.type === 'numericalscale'
                ? {
                      optionScores: getNumericalScaleOptionScores(
                          actions[blockActionId]
                      ),
                  }
                : {}),
        };
    });

    return blockAnswers;
}

export function questionSupportsAnswerHighlight(question) {
    const actions = question?.actions || question?.definition?.actions;
    return Boolean(actions && Object.keys(actions).length);
}
